/**
 * Fitness Questionnaire Validation Schemas
 *
 * Zod schema for the fitness questionnaire / plan-generation form. Mirrors the
 * backend `StoreFitnessQuestionaireRequest` rules so the client rejects the same
 * payloads the API would.
 *
 * Shape: a small **required spine** (goals, timeline, level, limitations, the
 * generate flag) plus a large **optional body** (everything else is nullable on
 * the backend). The single schema covers both a Phase 1 spine-only submit and a
 * Phase 2 "make it sharper" submit — the spine is always required.
 *
 * Notes mirrored from the backend rules:
 * - `primary_fitness_goals` / `equipment_on_hand` use the `EnumOrOther` rule:
 *   each item is a valid enum value OR free "other" text, so items validate as
 *   non-empty strings (the enum options drive the UI chips, not the constraint).
 * - `favourite_workouts` excludes `unknown`; `gender` excludes `prefer_not_to_say`
 *   (both enforced by the option sets the enums are built from).
 * - `motivation_other` / `biggest_challenge_other` are `required_if` their parent
 *   equals `other` (enforced via `superRefine`).
 */

import { z } from './zod';
import {
  createEnumFromOptions,
  getFitnessChallengeOptions,
  getFitnessLevelOptions,
  getGoalTimelineOptions,
  getMotivationOptions,
  getQuestionnaireGenderOptions,
  getTimeCommitmentOptions,
  getWorkoutFrequencyOptions,
  getWorkoutLocationOptions,
  getWorkoutTypeOptions,
} from '../utils/select-options';

// =============================================================================
// CONSTANTS
// =============================================================================

const MIN_AGE = 16;
const MIN_PRIORITY = 1;
const MAX_PRIORITY = 10;
const MAX_LIMITATION_LENGTH = 500;

/**
 * `EnumOrOther` item: a recognised enum value OR free "other" text. The backend
 * accepts any non-empty string, so that is the value-level constraint here.
 */
const enumOrOtherItem = z.string().min(1, 'Cannot be empty');

// =============================================================================
// FITNESS QUESTIONNAIRE SCHEMA
// =============================================================================

const questionnaireObject = z.object({
  // --- Required spine (gates the first plan) ------------------------------
  primary_fitness_goals: z
    .array(enumOrOtherItem)
    .min(1, 'Select at least one goal'),
  goal_timeline: createEnumFromOptions(getGoalTimelineOptions()),
  current_fitness_level: createEnumFromOptions(getFitnessLevelOptions()),
  has_limitations: z.boolean(),
  generate_personalized_plan: z.boolean(),

  // --- Optional body ("make it sharper") ----------------------------------
  goal_priority: z
    .number()
    .int()
    .min(MIN_PRIORITY)
    .max(MAX_PRIORITY)
    .optional(),
  limitation_description: z
    .string()
    .max(
      MAX_LIMITATION_LENGTH,
      `Keep it under ${MAX_LIMITATION_LENGTH} characters`
    )
    .optional(),
  age: z
    .number()
    .int()
    .min(MIN_AGE, `You must be at least ${MIN_AGE}`)
    .optional(),
  gender: createEnumFromOptions(getQuestionnaireGenderOptions()).optional(),
  height: z.number().positive().optional(),
  weight: z.number().positive().optional(),
  current_days_of_exercise: createEnumFromOptions(
    getWorkoutFrequencyOptions()
  ).optional(),
  minutes_of_workout: createEnumFromOptions(
    getTimeCommitmentOptions()
  ).optional(),
  favourite_workouts: z
    .array(createEnumFromOptions(getWorkoutTypeOptions()))
    .optional(),
  primary_workout_location: createEnumFromOptions(
    getWorkoutLocationOptions()
  ).optional(),
  equipment_on_hand: z.array(enumOrOtherItem).optional(),
  motivation: createEnumFromOptions(getMotivationOptions()).optional(),
  motivation_other: z.string().optional(),
  biggest_challenge: createEnumFromOptions(
    getFitnessChallengeOptions()
  ).optional(),
  biggest_challenge_other: z.string().optional(),
});

/** The backend's `required_if` rules for the two "other" free-text fields. */
function requireOtherText(
  data: z.output<typeof questionnaireObject>,
  ctx: z.RefinementCtx
): void {
  // required_if:motivation,other
  if (data.motivation === 'other' && !data.motivation_other?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['motivation_other'],
      message: 'Please tell us a bit more.',
    });
  }
  // required_if:biggest_challenge,other
  if (
    data.biggest_challenge === 'other' &&
    !data.biggest_challenge_other?.trim()
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['biggest_challenge_other'],
      message: 'Please tell us a bit more.',
    });
  }
}

export const fitnessQuestionnaireSchema =
  questionnaireObject.superRefine(requireOtherText);

/**
 * The sign-up questionnaire's rules: the API's, plus the four plan inputs the
 * generator sizes a plan by — where you train, what kit you have, how often and
 * for how long. The API keeps them optional (Adjust goals clears one by leaving
 * it out), but a plan generated without them falls back to bodyweight, three
 * days a week and a fixed six exercises, so sign-up asks for them.
 *
 * Equipment is the exception at the gym: the generator gives a gym plan the
 * full gym whatever is ticked, so a gym user may leave it empty.
 *
 * The four are checked by a refinement on a schema of their own, joined to the
 * API's with `.and()`: zod skips an object's refinements once any of its
 * fields is missing, so a refinement on the whole form would let a blank setup
 * step through whenever an earlier step is blank too.
 */
const setupAnswersSchema = questionnaireObject
  .pick({
    primary_workout_location: true,
    equipment_on_hand: true,
    current_days_of_exercise: true,
    minutes_of_workout: true,
  })
  .superRefine((data, ctx) => {
    const require = (
      field: keyof typeof data,
      missing: boolean,
      message: string
    ): void => {
      if (missing) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message });
      }
    };
    require('primary_workout_location', !data.primary_workout_location, 'Choose where you usually train');
    require('equipment_on_hand', data.primary_workout_location !== 'gym' &&
      (data.equipment_on_hand?.length ?? 0) === 0, 'Choose at least one');
    // "None" is no plan; sign-up does not offer it (a saved one is refused).
    require('current_days_of_exercise', !data.current_days_of_exercise ||
      data.current_days_of_exercise === '0', 'Choose how often you train');
    require('minutes_of_workout', !data.minutes_of_workout, 'Choose how long a session runs');
  });

export const signUpQuestionnaireSchema =
  fitnessQuestionnaireSchema.and(setupAnswersSchema);

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type FitnessQuestionnaireInput = z.input<
  typeof fitnessQuestionnaireSchema
>;
export type FitnessQuestionnaireOutput = z.output<
  typeof fitnessQuestionnaireSchema
>;

// =============================================================================
// VALIDATION HELPER FUNCTIONS
// =============================================================================

/**
 * Validate fitness questionnaire data
 * @throws ZodError if validation fails
 */
export function validateFitnessQuestionnaire(
  data: unknown
): FitnessQuestionnaireOutput {
  return fitnessQuestionnaireSchema.parse(data);
}

/**
 * Safely validate fitness questionnaire data
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateFitnessQuestionnaire(data: unknown) {
  return fitnessQuestionnaireSchema.safeParse(data);
}

/**
 * Check if data is valid fitness questionnaire data
 */
export function isValidFitnessQuestionnaire(data: unknown): boolean {
  return fitnessQuestionnaireSchema.safeParse(data).success;
}
